import { Component, Input } from '@angular/core';

export interface TableColumn {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  sticky?: boolean;
}

export interface TableCell {
  value: any;
  displayValue?: string;
  cssClass?: string;
  tooltip?: string;
  metadata?: any;
}

export interface TableRow {
  [key: string]: TableCell | string | number | any;
}

export interface TableConfig {
  height?: string;
  maxHeight?: string;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;
  responsive?: boolean;
}

@Component({
  selector: 'piqet-common-table',
  templateUrl: './common-table.component.html',
  styleUrls: ['./common-table.component.scss'],
})
export class CommonTableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() data: TableRow[] = [];
  @Input() config: TableConfig = {};
  @Input() loading = false;
  @Input() emptyMessage = 'No data available';
  @Input() rowHeaderKey?: string; // Key for the row header column
  @Input() customFormatter?: (value: any, column: TableColumn, row: TableRow) => string;

  getColumnStyle(column: TableColumn): any {
    const style: any = {};
    if (column.width) style.width = column.width;
    if (column.align) style.textAlign = column.align;
    return style;
  }

  getCellValue(row: TableRow, column: TableColumn): any {
    const cellData = row[column.key];

    // Handle TableCell interface
    if (cellData && typeof cellData === 'object' && 'value' in cellData) {
      return cellData.value;
    }

    return cellData;
  }

  getCellDisplayValue(row: TableRow, column: TableColumn): string {
    const cellData = row[column.key];

    // Handle TableCell interface with custom display value
    if (cellData && typeof cellData === 'object' && 'displayValue' in cellData) {
      return cellData.displayValue || this.formatValue(cellData.value);
    }

    // Use custom formatter if provided
    if (this.customFormatter) {
      return this.customFormatter(cellData, column, row);
    }

    return this.formatValue(cellData);
  }

  getCellClass(row: TableRow, column: TableColumn): string {
    const cellData = row[column.key];
    let classes = '';

    // Add row header class if this is the row header column
    if (this.rowHeaderKey && column.key === this.rowHeaderKey) {
      classes += 'row-header ';
    }

    // Handle TableCell interface with custom CSS class
    if (cellData && typeof cellData === 'object' && 'cssClass' in cellData) {
      classes += cellData.cssClass || '';
    }

    return classes.trim();
  }

  getCellTooltip(row: TableRow, column: TableColumn): string {
    const cellData = row[column.key];

    if (cellData && typeof cellData === 'object' && 'tooltip' in cellData && cellData.tooltip) {
      return cellData.tooltip;
    }

    // Show original backend values as tooltip
    if (cellData && typeof cellData === 'object' && cellData.metadata) {
      const { originalImpactValue } = cellData.metadata;

      if (originalImpactValue) {
        return `Impact Value: ${originalImpactValue}`;
      }
    }

    return '';
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      if (Math.abs(value) < 0.001 && value !== 0) {
        return value.toExponential(2);
      }
      return value.toString();
    }

    return String(value);
  }

  getTableClasses(): string {
    let classes = 'data-table';

    if (this.config.striped) classes += ' striped';
    if (this.config.hover) classes += ' hover';
    if (this.config.bordered) classes += ' bordered';

    return classes;
  }

  getContainerStyle(): any {
    const style: any = {};

    if (this.config.height) style.height = this.config.height;
    if (this.config.maxHeight) style.maxHeight = this.config.maxHeight;

    return style;
  }

  trackByFn(index: any, item: any) {
    return item.id || index;
  }
}
